import React, { useEffect, useState, useRef, memo, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Globe, Bot, Database, Mail, MousePointer2, Play, Pause, RotateCcw, ChevronLeft, Save, Webhook, MessageSquare, GitBranch, CalendarDays, HardDrive, FileText, Code2 } from 'lucide-react';
const STAGE_W = 1280;
const STAGE_H = 720;
// =================== Time markers (in seconds) ===================
const T = {
  SHELL_IN: 0.3,
  CAPTION_1: 1.2,
  // Phone Call
  CURSOR_IN: 3.0,
  PHONE_HOVER: 3.6,
  PHONE_CLICK: 4.1,
  PHONE_DRAG_START: 4.3,
  PHONE_DROP: 5.8,
  PHONE_TAG: 6.0,
  // Website Widget
  WIDGET_HOVER_START: 7.0,
  WIDGET_HOVER: 7.5,
  WIDGET_CLICK: 8.0,
  WIDGET_DRAG_START: 8.2,
  WIDGET_DROP: 9.6,
  // AI Agent
  AGENT_HOVER_START: 10.6,
  AGENT_HOVER: 11.2,
  AGENT_CLICK: 11.7,
  AGENT_DRAG_START: 11.9,
  AGENT_DROP: 13.4,
  AGENT_TAG: 13.6,
  // Connect 1: Phone -> Agent
  C1_TO_HANDLE: 14.6,
  C1_GRAB: 15.1,
  C1_DRAG: 15.3,
  C1_DROP: 16.5,
  // Connect 2: Widget -> Agent
  C2_TO_HANDLE: 17.0,
  C2_GRAB: 17.4,
  C2_DRAG: 17.6,
  C2_DROP: 18.7,
  // Knowledge Base
  KB_HOVER_START: 19.4,
  KB_HOVER: 19.9,
  KB_CLICK: 20.3,
  KB_DRAG_START: 20.5,
  KB_DROP: 21.9,
  KB_TAG: 22.1,
  // Connect 3: Agent -> KB
  C3_TO_HANDLE: 23.0,
  C3_GRAB: 23.4,
  C3_DRAG: 23.6,
  C3_DROP: 24.7,
  // Send Email
  EMAIL_HOVER_START: 25.4,
  EMAIL_HOVER: 25.9,
  EMAIL_CLICK: 26.3,
  EMAIL_DRAG_START: 26.5,
  EMAIL_DROP: 27.9,
  EMAIL_TAG: 28.1,
  // Connect 4: Agent -> Email
  C4_TO_HANDLE: 28.9,
  C4_GRAB: 29.3,
  C4_DRAG: 29.5,
  C4_DROP: 30.6,
  // Glow + closing
  CURSOR_OUT: 31.2,
  FLOW_GLOW: 31.5,
  CAPTION_2A: 32.4,
  CAPTION_2B: 33.6,
  // Impact
  IMPACT: 34.6,
  IMPACT_1: 35.0,
  IMPACT_2: 35.7,
  IMPACT_3: 36.4,
  IMPACT_FINAL: 37.2
};
export const SLIDE_7_DURATION = 38.5;
// =================== Sidebar items (faithful to real FlowSidebar) ===================
type SidebarColor = 'emerald' | 'purple' | 'teal' | 'orange' | 'amber';
interface SidebarItem {
  id: string;
  label: string;
  icon: React.ElementType;
  color: SidebarColor;
}
interface SidebarSection {
  category: string;
  items: SidebarItem[];
}
const SIDEBAR: SidebarSection[] = [{
  category: 'CHANNELS',
  items: [{
    id: 'webhook',
    label: 'Webhook',
    icon: Webhook,
    color: 'emerald'
  }, {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: MessageSquare,
    color: 'emerald'
  }, {
    id: 'phone',
    label: 'Phone Call',
    icon: Phone,
    color: 'emerald'
  }, {
    id: 'widget',
    label: 'Website Widget',
    icon: Globe,
    color: 'emerald'
  }]
}, {
  category: 'AGENTS',
  items: [{
    id: 'agent',
    label: 'AI Agent',
    icon: Bot,
    color: 'purple'
  }]
}, {
  category: 'KNOWLEDGE',
  items: [{
    id: 'kb',
    label: 'Knowledge Base',
    icon: Database,
    color: 'teal'
  }]
}, {
  category: 'CONDITIONS',
  items: [{
    id: 'condition',
    label: 'Condition',
    icon: GitBranch,
    color: 'amber'
  }]
}, {
  category: 'ACTIONS',
  items: [{
    id: 'http',
    label: 'HTTP Request',
    icon: Globe,
    color: 'orange'
  }, {
    id: 'email',
    label: 'Send Email',
    icon: Mail,
    color: 'orange'
  }, {
    id: 'action-phone',
    label: 'Phone Call',
    icon: Phone,
    color: 'orange'
  }, {
    id: 'action-whatsapp',
    label: 'Send WhatsApp',
    icon: MessageSquare,
    color: 'orange'
  }, {
    id: 'calendar',
    label: 'Calendar Event',
    icon: CalendarDays,
    color: 'orange'
  }, {
    id: 'drive',
    label: 'Google Drive',
    icon: HardDrive,
    color: 'orange'
  }, {
    id: 'log',
    label: 'Simple Log',
    icon: FileText,
    color: 'orange'
  }, {
    id: 'python',
    label: 'Custom Python',
    icon: Code2,
    color: 'orange'
  }]
}];
const COLOR_MAP: Record<SidebarColor, {
  cardBg: string;
  cardBorder: string;
  iconBg: string;
  iconText: string;
  pillBg: string;
}> = {
  emerald: {
    cardBg: 'bg-white',
    cardBorder: 'border-emerald-100',
    iconBg: 'bg-emerald-50',
    iconText: 'text-emerald-600',
    pillBg: 'bg-emerald-50'
  },
  purple: {
    cardBg: 'bg-purple-50/40',
    cardBorder: 'border-purple-200',
    iconBg: 'bg-purple-100',
    iconText: 'text-purple-600',
    pillBg: 'bg-purple-50'
  },
  teal: {
    cardBg: 'bg-white',
    cardBorder: 'border-teal-100',
    iconBg: 'bg-teal-50',
    iconText: 'text-teal-600',
    pillBg: 'bg-teal-50'
  },
  orange: {
    cardBg: 'bg-white',
    cardBorder: 'border-orange-100',
    iconBg: 'bg-orange-50',
    iconText: 'text-orange-600',
    pillBg: 'bg-orange-50'
  },
  amber: {
    cardBg: 'bg-white',
    cardBorder: 'border-amber-100',
    iconBg: 'bg-amber-50',
    iconText: 'text-amber-600',
    pillBg: 'bg-amber-50'
  }
};
// =================== Layout constants ===================
// App shell: 32px margin all around 1280x720 stage = inset 32, so shell is 1216x656
const SHELL_X = 32;
const SHELL_Y = 32;
const SHELL_W = STAGE_W - 64;
const SHELL_H = STAGE_H - 64;
const TOPBAR_H = 44;
const SIDEBAR_W = 240;
const CANVAS_X = SHELL_X + SIDEBAR_W;
const CANVAS_Y = SHELL_Y + TOPBAR_H;
const CANVAS_W = SHELL_W - SIDEBAR_W;
const CANVAS_H = SHELL_H - TOPBAR_H;
// Sidebar item Y offsets (relative to sidebar top, which is SHELL_Y + TOPBAR_H)
// Layout in sidebar: p-3 (12), then sections with category label (~24) + items (~40 each) + spacing
const SIDEBAR_ITEM_POS: Record<string, {
  x: number;
  y: number;
}> = {
  // CHANNELS (starts after p-3 + label)
  webhook: {
    x: SHELL_X + SIDEBAR_W / 2,
    y: SHELL_Y + TOPBAR_H + 56
  },
  whatsapp: {
    x: SHELL_X + SIDEBAR_W / 2,
    y: SHELL_Y + TOPBAR_H + 94
  },
  phone: {
    x: SHELL_X + SIDEBAR_W / 2,
    y: SHELL_Y + TOPBAR_H + 132
  },
  widget: {
    x: SHELL_X + SIDEBAR_W / 2,
    y: SHELL_Y + TOPBAR_H + 170
  },
  // AGENTS
  agent: {
    x: SHELL_X + SIDEBAR_W / 2,
    y: SHELL_Y + TOPBAR_H + 242
  },
  // KNOWLEDGE
  kb: {
    x: SHELL_X + SIDEBAR_W / 2,
    y: SHELL_Y + TOPBAR_H + 314
  },
  // CONDITIONS (added: shifts ACTIONS down)
  // ACTIONS — Send Email is the 2nd item (after HTTP Request)
  email: {
    x: SHELL_X + SIDEBAR_W / 2,
    y: SHELL_Y + TOPBAR_H + 502
  }
};
// Node positions in canvas-local coords (0..CANVAS_W, 0..CANVAS_H)
// Node card is 240px wide, ~84px tall. Coordinates = top-left of card.
const NODE_W = 240;
const NODE_H = 84;
interface NodeDef {
  id: string;
  label: string;
  sub: string;
  icon: React.ElementType;
  color: SidebarColor;
  cx: number; // canvas-local
  cy: number;
}
const NODES: Record<string, NodeDef> = {
  phone: {
    id: 'phone',
    label: 'Phone Call',
    sub: '+971 800 8388',
    icon: Phone,
    color: 'emerald',
    cx: 80,
    cy: 100
  },
  widget: {
    id: 'widget',
    label: 'Website Widget',
    sub: 'vexu.ae widget',
    icon: Globe,
    color: 'emerald',
    cx: 80,
    cy: 280
  },
  agent: {
    id: 'agent',
    label: 'AI Agent',
    sub: 'You are a helpful UAE assistant…',
    icon: Bot,
    color: 'purple',
    cx: 420,
    cy: 200
  },
  kb: {
    id: 'kb',
    label: 'Knowledge Base',
    sub: 'Al Falah Group · Policies',
    icon: Database,
    color: 'teal',
    cx: 420,
    cy: 410
  },
  email: {
    id: 'email',
    label: 'Send Email',
    sub: 'support@vexu.ae',
    icon: Mail,
    color: 'orange',
    cx: 760,
    cy: 200
  }
};
// Convert canvas-local node coords -> stage coords for cursor positioning
function nodeStageX(n: NodeDef) {
  return CANVAS_X + n.cx;
}
function nodeStageY(n: NodeDef) {
  return CANVAS_Y + n.cy;
}
function nodeCenterStage(n: NodeDef) {
  return {
    x: nodeStageX(n) + NODE_W / 2,
    y: nodeStageY(n) + NODE_H / 2
  };
}
function nodeRightHandleStage(n: NodeDef) {
  return {
    x: nodeStageX(n) + NODE_W,
    y: nodeStageY(n) + NODE_H / 2
  };
}
function nodeLeftHandleStage(n: NodeDef) {
  return {
    x: nodeStageX(n),
    y: nodeStageY(n) + NODE_H / 2
  };
}
// =================== Math helpers ===================
function lerp(a: {
  x: number;
  y: number;
}, b: {
  x: number;
  y: number;
}, p: number) {
  return {
    x: a.x + (b.x - a.x) * p,
    y: a.y + (b.y - a.y) * p
  };
}
function easeInOut(t: number) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}
function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}
function progress(t: number, a: number, b: number) {
  if (t <= a) return 0;
  if (t >= b) return 1;
  return easeInOut((t - a) / (b - a));
}
function bezierPath(from: {
  x: number;
  y: number;
}, to: {
  x: number;
  y: number;
}) {
  const dx = Math.max(40, Math.abs(to.x - from.x) * 0.5);
  return `M ${from.x} ${from.y} C ${from.x + dx} ${from.y}, ${to.x - dx} ${to.y}, ${to.x} ${to.y}`;
}
// =================== Cursor state machine ===================
function getCursorPosition(t: number): {
  x: number;
  y: number;
  visible: boolean;
  clicking: boolean;
} {
  // Off-stage default
  let pos = {
    x: STAGE_W + 40,
    y: -40
  };
  let visible = false;
  let clicking = false;
  const phoneSidebar = SIDEBAR_ITEM_POS.phone;
  const widgetSidebar = SIDEBAR_ITEM_POS.widget;
  const agentSidebar = SIDEBAR_ITEM_POS.agent;
  const kbSidebar = SIDEBAR_ITEM_POS.kb;
  const emailSidebar = SIDEBAR_ITEM_POS.email;
  const phoneCenter = nodeCenterStage(NODES.phone);
  const widgetCenter = nodeCenterStage(NODES.widget);
  const agentCenter = nodeCenterStage(NODES.agent);
  const kbCenter = nodeCenterStage(NODES.kb);
  const emailCenter = nodeCenterStage(NODES.email);
  if (t >= T.CURSOR_IN && t < T.CURSOR_OUT) visible = true;
  // Phase 1: Cursor enter -> phone sidebar item
  if (t >= T.CURSOR_IN && t < T.PHONE_HOVER) {
    const p = progress(t, T.CURSOR_IN, T.PHONE_HOVER);
    pos = lerp({
      x: STAGE_W + 40,
      y: 100
    }, phoneSidebar, p);
  } else if (t >= T.PHONE_HOVER && t < T.PHONE_CLICK) {
    pos = phoneSidebar;
    if (t >= T.PHONE_CLICK - 0.15) clicking = true;
  } else if (t >= T.PHONE_CLICK && t < T.PHONE_DRAG_START) {
    pos = phoneSidebar;
    clicking = t < T.PHONE_CLICK + 0.15;
  } else if (t >= T.PHONE_DRAG_START && t < T.PHONE_DROP) {
    const p = progress(t, T.PHONE_DRAG_START, T.PHONE_DROP);
    pos = lerp(phoneSidebar, phoneCenter, p);
  }
  // Phase 2: hold a beat then move to widget
  else if (t >= T.PHONE_DROP && t < T.WIDGET_HOVER_START) {
    pos = phoneCenter;
  } else if (t >= T.WIDGET_HOVER_START && t < T.WIDGET_HOVER) {
    const p = progress(t, T.WIDGET_HOVER_START, T.WIDGET_HOVER);
    pos = lerp(phoneCenter, widgetSidebar, p);
  } else if (t >= T.WIDGET_HOVER && t < T.WIDGET_CLICK) {
    pos = widgetSidebar;
    if (t >= T.WIDGET_CLICK - 0.15) clicking = true;
  } else if (t >= T.WIDGET_CLICK && t < T.WIDGET_DRAG_START) {
    pos = widgetSidebar;
    clicking = t < T.WIDGET_CLICK + 0.15;
  } else if (t >= T.WIDGET_DRAG_START && t < T.WIDGET_DROP) {
    const p = progress(t, T.WIDGET_DRAG_START, T.WIDGET_DROP);
    pos = lerp(widgetSidebar, widgetCenter, p);
  }
  // Phase 3: agent
  else if (t >= T.WIDGET_DROP && t < T.AGENT_HOVER_START) {
    pos = widgetCenter;
  } else if (t >= T.AGENT_HOVER_START && t < T.AGENT_HOVER) {
    const p = progress(t, T.AGENT_HOVER_START, T.AGENT_HOVER);
    pos = lerp(widgetCenter, agentSidebar, p);
  } else if (t >= T.AGENT_HOVER && t < T.AGENT_CLICK) {
    pos = agentSidebar;
    if (t >= T.AGENT_CLICK - 0.15) clicking = true;
  } else if (t >= T.AGENT_CLICK && t < T.AGENT_DRAG_START) {
    pos = agentSidebar;
    clicking = t < T.AGENT_CLICK + 0.15;
  } else if (t >= T.AGENT_DRAG_START && t < T.AGENT_DROP) {
    const p = progress(t, T.AGENT_DRAG_START, T.AGENT_DROP);
    pos = lerp(agentSidebar, agentCenter, p);
  }
  // Connect 1: phone -> agent
  else if (t >= T.AGENT_DROP && t < T.C1_TO_HANDLE) {
    pos = agentCenter;
  } else if (t >= T.C1_TO_HANDLE && t < T.C1_GRAB) {
    const p = progress(t, T.C1_TO_HANDLE, T.C1_GRAB);
    pos = lerp(agentCenter, nodeRightHandleStage(NODES.phone), p);
  } else if (t >= T.C1_GRAB && t < T.C1_DRAG) {
    pos = nodeRightHandleStage(NODES.phone);
    clicking = true;
  } else if (t >= T.C1_DRAG && t < T.C1_DROP) {
    const p = progress(t, T.C1_DRAG, T.C1_DROP);
    pos = lerp(nodeRightHandleStage(NODES.phone), nodeLeftHandleStage(NODES.agent), p);
    clicking = true;
  }
  // Connect 2: widget -> agent
  else if (t >= T.C1_DROP && t < T.C2_TO_HANDLE) {
    pos = nodeLeftHandleStage(NODES.agent);
  } else if (t >= T.C2_TO_HANDLE && t < T.C2_GRAB) {
    const p = progress(t, T.C2_TO_HANDLE, T.C2_GRAB);
    pos = lerp(nodeLeftHandleStage(NODES.agent), nodeRightHandleStage(NODES.widget), p);
  } else if (t >= T.C2_GRAB && t < T.C2_DRAG) {
    pos = nodeRightHandleStage(NODES.widget);
    clicking = true;
  } else if (t >= T.C2_DRAG && t < T.C2_DROP) {
    const p = progress(t, T.C2_DRAG, T.C2_DROP);
    pos = lerp(nodeRightHandleStage(NODES.widget), nodeLeftHandleStage(NODES.agent), p);
    clicking = true;
  }
  // KB
  else if (t >= T.C2_DROP && t < T.KB_HOVER_START) {
    pos = nodeLeftHandleStage(NODES.agent);
  } else if (t >= T.KB_HOVER_START && t < T.KB_HOVER) {
    const p = progress(t, T.KB_HOVER_START, T.KB_HOVER);
    pos = lerp(nodeLeftHandleStage(NODES.agent), kbSidebar, p);
  } else if (t >= T.KB_HOVER && t < T.KB_CLICK) {
    pos = kbSidebar;
    if (t >= T.KB_CLICK - 0.15) clicking = true;
  } else if (t >= T.KB_CLICK && t < T.KB_DRAG_START) {
    pos = kbSidebar;
    clicking = t < T.KB_CLICK + 0.15;
  } else if (t >= T.KB_DRAG_START && t < T.KB_DROP) {
    const p = progress(t, T.KB_DRAG_START, T.KB_DROP);
    pos = lerp(kbSidebar, kbCenter, p);
  }
  // Connect 3: agent -> kb
  else if (t >= T.KB_DROP && t < T.C3_TO_HANDLE) {
    pos = kbCenter;
  } else if (t >= T.C3_TO_HANDLE && t < T.C3_GRAB) {
    const p = progress(t, T.C3_TO_HANDLE, T.C3_GRAB);
    pos = lerp(kbCenter, nodeRightHandleStage(NODES.agent), p);
  } else if (t >= T.C3_GRAB && t < T.C3_DRAG) {
    pos = nodeRightHandleStage(NODES.agent);
    clicking = true;
  } else if (t >= T.C3_DRAG && t < T.C3_DROP) {
    const p = progress(t, T.C3_DRAG, T.C3_DROP);
    pos = lerp(nodeRightHandleStage(NODES.agent), nodeLeftHandleStage(NODES.kb), p);
    clicking = true;
  }
  // Email
  else if (t >= T.C3_DROP && t < T.EMAIL_HOVER_START) {
    pos = nodeLeftHandleStage(NODES.kb);
  } else if (t >= T.EMAIL_HOVER_START && t < T.EMAIL_HOVER) {
    const p = progress(t, T.EMAIL_HOVER_START, T.EMAIL_HOVER);
    pos = lerp(nodeLeftHandleStage(NODES.kb), emailSidebar, p);
  } else if (t >= T.EMAIL_HOVER && t < T.EMAIL_CLICK) {
    pos = emailSidebar;
    if (t >= T.EMAIL_CLICK - 0.15) clicking = true;
  } else if (t >= T.EMAIL_CLICK && t < T.EMAIL_DRAG_START) {
    pos = emailSidebar;
    clicking = t < T.EMAIL_CLICK + 0.15;
  } else if (t >= T.EMAIL_DRAG_START && t < T.EMAIL_DROP) {
    const p = progress(t, T.EMAIL_DRAG_START, T.EMAIL_DROP);
    pos = lerp(emailSidebar, emailCenter, p);
  }
  // Connect 4: agent -> email
  else if (t >= T.EMAIL_DROP && t < T.C4_TO_HANDLE) {
    pos = emailCenter;
  } else if (t >= T.C4_TO_HANDLE && t < T.C4_GRAB) {
    const p = progress(t, T.C4_TO_HANDLE, T.C4_GRAB);
    pos = lerp(emailCenter, nodeRightHandleStage(NODES.agent), p);
  } else if (t >= T.C4_GRAB && t < T.C4_DRAG) {
    pos = nodeRightHandleStage(NODES.agent);
    clicking = true;
  } else if (t >= T.C4_DRAG && t < T.C4_DROP) {
    const p = progress(t, T.C4_DRAG, T.C4_DROP);
    pos = lerp(nodeRightHandleStage(NODES.agent), nodeLeftHandleStage(NODES.email), p);
    clicking = true;
  }
  // Cursor exit
  else if (t >= T.C4_DROP && t < T.CURSOR_OUT) {
    const p = progress(t, T.C4_DROP, T.CURSOR_OUT);
    pos = lerp(nodeLeftHandleStage(NODES.email), {
      x: STAGE_W + 40,
      y: 100
    }, p);
  }
  return {
    x: pos.x,
    y: pos.y,
    visible,
    clicking
  };
}
// =================== Drag ghost (sidebar pill that follows cursor during drag) ===================
function getDragGhost(t: number): {
  item: SidebarItem;
  x: number;
  y: number;
} | null {
  if (t >= T.PHONE_DRAG_START && t < T.PHONE_DROP) {
    const p = progress(t, T.PHONE_DRAG_START, T.PHONE_DROP);
    const pos = lerp(SIDEBAR_ITEM_POS.phone, nodeCenterStage(NODES.phone), p);
    return {
      item: SIDEBAR[0].items[2],
      x: pos.x,
      y: pos.y
    };
  }
  if (t >= T.WIDGET_DRAG_START && t < T.WIDGET_DROP) {
    const p = progress(t, T.WIDGET_DRAG_START, T.WIDGET_DROP);
    const pos = lerp(SIDEBAR_ITEM_POS.widget, nodeCenterStage(NODES.widget), p);
    return {
      item: SIDEBAR[0].items[3],
      x: pos.x,
      y: pos.y
    };
  }
  if (t >= T.AGENT_DRAG_START && t < T.AGENT_DROP) {
    const p = progress(t, T.AGENT_DRAG_START, T.AGENT_DROP);
    const pos = lerp(SIDEBAR_ITEM_POS.agent, nodeCenterStage(NODES.agent), p);
    return {
      item: SIDEBAR[1].items[0],
      x: pos.x,
      y: pos.y
    };
  }
  if (t >= T.KB_DRAG_START && t < T.KB_DROP) {
    const p = progress(t, T.KB_DRAG_START, T.KB_DROP);
    const pos = lerp(SIDEBAR_ITEM_POS.kb, nodeCenterStage(NODES.kb), p);
    return {
      item: SIDEBAR[2].items[0],
      x: pos.x,
      y: pos.y
    };
  }
  if (t >= T.EMAIL_DRAG_START && t < T.EMAIL_DROP) {
    const p = progress(t, T.EMAIL_DRAG_START, T.EMAIL_DROP);
    const pos = lerp(SIDEBAR_ITEM_POS.email, nodeCenterStage(NODES.email), p);
    return {
      item: SIDEBAR[4].items[1],
      x: pos.x,
      y: pos.y
    };
  }
  return null;
}
// =================== Active connection line being drawn (in canvas-local coords) ===================
function getDrawingEdge(t: number): {
  from: {
    x: number;
    y: number;
  };
  to: {
    x: number;
    y: number;
  };
} | null {
  const toLocal = (s: {
    x: number;
    y: number;
  }) => ({
    x: s.x - CANVAS_X,
    y: s.y - CANVAS_Y
  });
  if (t >= T.C1_DRAG && t < T.C1_DROP) {
    const p = progress(t, T.C1_DRAG, T.C1_DROP);
    const from = toLocal(nodeRightHandleStage(NODES.phone));
    const target = toLocal(nodeLeftHandleStage(NODES.agent));
    return {
      from,
      to: lerp(from, target, p)
    };
  }
  if (t >= T.C2_DRAG && t < T.C2_DROP) {
    const p = progress(t, T.C2_DRAG, T.C2_DROP);
    const from = toLocal(nodeRightHandleStage(NODES.widget));
    const target = toLocal(nodeLeftHandleStage(NODES.agent));
    return {
      from,
      to: lerp(from, target, p)
    };
  }
  if (t >= T.C3_DRAG && t < T.C3_DROP) {
    const p = progress(t, T.C3_DRAG, T.C3_DROP);
    const from = toLocal(nodeRightHandleStage(NODES.agent));
    const target = toLocal(nodeLeftHandleStage(NODES.kb));
    return {
      from,
      to: lerp(from, target, p)
    };
  }
  if (t >= T.C4_DRAG && t < T.C4_DROP) {
    const p = progress(t, T.C4_DRAG, T.C4_DROP);
    const from = toLocal(nodeRightHandleStage(NODES.agent));
    const target = toLocal(nodeLeftHandleStage(NODES.email));
    return {
      from,
      to: lerp(from, target, p)
    };
  }
  return null;
}
// =================== Captions ===================
function getCaption(t: number): {
  id: string;
  text: string;
} | null {
  if (t < T.CAPTION_1) return null;
  if (t < T.CURSOR_IN) return {
    id: 'a',
    text: 'Build your logic visually.'
  };
  if (t < T.CAPTION_2A) return null;
  if (t < T.CAPTION_2B) return {
    id: 'b',
    text: 'Calls. Chat. Knowledge. Actions.'
  };
  if (t < T.IMPACT) return {
    id: 'c',
    text: 'All connected.'
  };
  return null;
}
// =================== Floating tags ===================
function getFloatingTag(t: number): {
  x: number;
  y: number;
  text: string;
  key: string;
} | null {
  if (t >= T.PHONE_TAG && t < T.PHONE_TAG + 1.6) {
    const n = NODES.phone;
    return {
      x: nodeStageX(n) + NODE_W + 16,
      y: nodeStageY(n) + 24,
      text: 'Start with any channel',
      key: 'phone-tag'
    };
  }
  if (t >= T.AGENT_TAG && t < T.AGENT_TAG + 1.8) {
    const n = NODES.agent;
    return {
      x: nodeStageX(n) + NODE_W / 2 - 90,
      y: nodeStageY(n) - 36,
      text: 'One AI handles everything.',
      key: 'agent-tag'
    };
  }
  if (t >= T.KB_TAG && t < T.KB_TAG + 1.6) {
    const n = NODES.kb;
    return {
      x: nodeStageX(n) + NODE_W + 16,
      y: nodeStageY(n) + 24,
      text: 'Powered by your data',
      key: 'kb-tag'
    };
  }
  if (t >= T.EMAIL_TAG && t < T.EMAIL_TAG + 1.6) {
    const n = NODES.email;
    return {
      x: nodeStageX(n) + NODE_W / 2 - 70,
      y: nodeStageY(n) - 36,
      text: 'Trigger real actions',
      key: 'email-tag'
    };
  }
  return null;
}
// =================== Components ===================
const FlowNode = memo(({
  node,
  glow,
  dropTime,
  t
}: {
  node: NodeDef;
  glow: boolean;
  dropTime: number;
  t: number;
}) => {
  const c = COLOR_MAP[node.color];
  const Icon = node.icon;
  const sinceDrop = t - dropTime;
  const flashOpacity = sinceDrop < 0.6 ? clamp01(1 - sinceDrop / 0.6) * 0.8 : 0;
  return <motion.div className="absolute" style={{
    left: node.cx,
    top: node.cy,
    zIndex: 5,
    width: NODE_W
  }} initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.35,
    ease: [0.16, 1, 0.3, 1]
  }} data-id="element-2347">
        <div className={`relative rounded-2xl border-2 shadow-sm ${c.cardBg} ${c.cardBorder} transition-all duration-500`} style={{
      boxShadow: glow ? '0 0 0 4px rgba(16, 217, 106, 0.12), 0 8px 24px -8px rgba(16, 217, 106, 0.3)' : undefined
    }} data-id="element-2348">
          {/* Handles */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-white border-2 border-gray-300" data-id="element-2349" />
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 rounded-full bg-white border-2 border-gray-300" data-id="element-2350" />

          <div className="p-4 flex items-start gap-3" data-id="element-2351">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${c.iconBg}`} data-id="element-2352">
              <Icon className={`w-4 h-4 ${c.iconText}`} data-id="element-2353" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-2354">
              <div className="font-semibold text-sm text-gray-900 truncate" data-id="element-2355">
                {node.label}
              </div>
              <div className="text-xs text-gray-500 mt-1 truncate leading-relaxed" data-id="element-2356">
                {node.sub}
              </div>
            </div>
          </div>

          {/* Drop flash */}
          {flashOpacity > 0 && <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
        boxShadow: `0 0 0 6px rgba(16, 217, 106, ${flashOpacity * 0.4}), 0 0 24px rgba(16, 217, 106, ${flashOpacity})`
      }} data-id="element-2357" />}
        </div>
      </motion.div>;
});
FlowNode.displayName = 'FlowNode';
const PermanentEdge = memo(({
  from,
  to,
  drawTime,
  glow,
  t
}: {
  from: {
    x: number;
    y: number;
  };
  to: {
    x: number;
    y: number;
  };
  drawTime: number;
  glow: boolean;
  t: number;
}) => {
  const path = bezierPath(from, to);
  const len = Math.hypot(to.x - from.x, to.y - from.y) * 1.2;
  const drawProgress = clamp01((t - drawTime) / 0.4);
  const offset = len * (1 - drawProgress);
  return <g data-id="element-2358">
        <path d={path} stroke="#10D96A" strokeWidth={4} opacity={glow ? 0.25 : 0.15} fill="none" data-id="element-2359" />
        <path d={path} stroke="#10D96A" strokeWidth={2} fill="none" strokeDasharray={len} strokeDashoffset={offset} opacity={0.85} data-id="element-2360" />
      </g>;
});
PermanentEdge.displayName = 'PermanentEdge';
const FlowShimmer = memo(({
  from,
  to,
  delay
}: {
  from: {
    x: number;
    y: number;
  };
  to: {
    x: number;
    y: number;
  };
  delay: number;
}) => {
  const path = bezierPath(from, to);
  return <motion.circle r={3} fill="#ffffff" style={{
    filter: 'drop-shadow(0 0 4px rgba(16,217,106,0.9))',
    offsetPath: `path('${path}')`
  }} initial={{
    offsetDistance: '0%',
    opacity: 0
  }} animate={{
    offsetDistance: '100%',
    opacity: [0, 1, 1, 0]
  }} transition={{
    duration: 2.2,
    repeat: Infinity,
    ease: 'linear',
    delay
  }} data-id="element-2361" />;
});
FlowShimmer.displayName = 'FlowShimmer';
const SidebarPill = memo(({
  item,
  hover,
  ghost = false
}: {
  item: SidebarItem;
  hover: boolean;
  ghost?: boolean;
}) => {
  const c = COLOR_MAP[item.color];
  const Icon = item.icon;
  return <div className={`flex items-center gap-2.5 py-2 px-3 rounded-lg transition-all ${ghost ? 'bg-white shadow-xl border border-gray-200' : hover ? 'bg-gray-50 scale-[1.02]' : ''}`} style={ghost ? {
    boxShadow: '0 12px 32px -8px rgba(0,0,0,0.25)'
  } : undefined} data-id="element-2362">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${c.pillBg}`} data-id="element-2363">
          <Icon className={`w-3.5 h-3.5 ${c.iconText}`} data-id="element-2364" />
        </div>
        <div className="text-xs font-medium text-gray-700 truncate" data-id="element-2365">
          {item.label}
        </div>
      </div>;
});
SidebarPill.displayName = 'SidebarPill';
const ImpactWord = memo(({
  text,
  start,
  t,
  size = 'normal',
  color = 'text-white'
}: {
  text: string;
  start: number;
  t: number;
  size?: 'normal' | 'large';
  color?: string;
}) => {
  const words = text.split(' ');
  return <div className={`flex items-center gap-3 ${size === 'large' ? 'text-6xl' : 'text-4xl'} font-light tracking-tight ${color}`} data-id="element-2366">
        {words.map((w, i) => {
      const ws = start + i * 0.08;
      const p = clamp01((t - ws) / 0.35);
      return <motion.span key={i} style={{
        filter: `blur(${(1 - p) * 14}px)`,
        opacity: p
      }} data-id="element-2367">
              {w}
            </motion.span>;
    })}
      </div>;
});
ImpactWord.displayName = 'ImpactWord';
// =================== Main slide ===================
interface PresentationSlide7Props {
  externalElapsed?: number;
  hideChrome?: boolean;
}
export function PresentationSlide7({
  externalElapsed,
  hideChrome = false
}: PresentationSlide7Props) {
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(true);
  const startedAtRef = useRef<number>(Date.now());
  const pausedAtRef = useRef<number>(0);
  const t = externalElapsed !== undefined ? externalElapsed : elapsed;
  useEffect(() => {
    if (externalElapsed !== undefined) return;
    if (!playing) {
      pausedAtRef.current = elapsed;
      return;
    }
    startedAtRef.current = Date.now() - pausedAtRef.current * 1000;
    let raf: number;
    const tick = () => {
      const newT = (Date.now() - startedAtRef.current) / 1000;
      if (newT >= SLIDE_7_DURATION) {
        setElapsed(SLIDE_7_DURATION);
        setPlaying(false);
        return;
      }
      setElapsed(newT);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, externalElapsed]);
  const replay = () => {
    pausedAtRef.current = 0;
    setElapsed(0);
    setPlaying(true);
  };
  // Stage scale
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const compute = () => {
      const s = Math.min(window.innerWidth / STAGE_W, window.innerHeight / STAGE_H);
      setScale(s);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);
  const cursor = getCursorPosition(t);
  const ghost = getDragGhost(t);
  const drawing = getDrawingEdge(t);
  const caption = getCaption(t);
  const tag = getFloatingTag(t);
  // Active nodes
  const showPhone = t >= T.PHONE_DROP;
  const showWidget = t >= T.WIDGET_DROP;
  const showAgent = t >= T.AGENT_DROP;
  const showKb = t >= T.KB_DROP;
  const showEmail = t >= T.EMAIL_DROP;
  // Permanent edges (canvas-local)
  const phoneR = {
    x: NODES.phone.cx + NODE_W,
    y: NODES.phone.cy + NODE_H / 2
  };
  const widgetR = {
    x: NODES.widget.cx + NODE_W,
    y: NODES.widget.cy + NODE_H / 2
  };
  const agentL = {
    x: NODES.agent.cx,
    y: NODES.agent.cy + NODE_H / 2
  };
  const agentR = {
    x: NODES.agent.cx + NODE_W,
    y: NODES.agent.cy + NODE_H / 2
  };
  const kbL = {
    x: NODES.kb.cx,
    y: NODES.kb.cy + NODE_H / 2
  };
  const emailL = {
    x: NODES.email.cx,
    y: NODES.email.cy + NODE_H / 2
  };
  const flowGlow = t >= T.FLOW_GLOW && t < T.IMPACT;
  const impactMode = t >= T.IMPACT;
  // Hover state for sidebar items
  const sidebarHover = (id: string): boolean => {
    if (id === 'phone' && t >= T.PHONE_HOVER && t < T.PHONE_DRAG_START) return true;
    if (id === 'widget' && t >= T.WIDGET_HOVER && t < T.WIDGET_DRAG_START) return true;
    if (id === 'agent' && t >= T.AGENT_HOVER && t < T.AGENT_DRAG_START) return true;
    if (id === 'kb' && t >= T.KB_HOVER && t < T.KB_DRAG_START) return true;
    if (id === 'email' && t >= T.EMAIL_HOVER && t < T.EMAIL_DRAG_START) return true;
    return false;
  };
  return <div className="relative w-screen h-screen overflow-hidden bg-[#06080F]" data-id="element-2368">
      {/* Ambient blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-2369">
        <motion.div className="absolute w-[800px] h-[800px] rounded-full opacity-[0.04]" style={{
        background: 'radial-gradient(circle, #10D96A 0%, transparent 70%)',
        left: '8%',
        top: '15%'
      }} animate={{
        scale: [1, 1.1, 1]
      }} transition={{
        duration: 9,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-2370" />
        <motion.div className="absolute w-[600px] h-[600px] rounded-full opacity-[0.025]" style={{
        background: 'radial-gradient(circle, #A855F7 0%, transparent 70%)',
        right: '10%',
        bottom: '10%'
      }} animate={{
        scale: [1, 1.15, 1]
      }} transition={{
        duration: 11,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-2371" />
      </div>

      {/* Scaled stage */}
      <div className="absolute inset-0 flex items-center justify-center" data-id="element-2372">
        <div className="relative origin-center" style={{
        width: STAGE_W,
        height: STAGE_H,
        transform: `scale(${scale})`
      }} data-id="element-2373">
          {/* App shell */}
          <motion.div className="absolute bg-white rounded-2xl overflow-hidden shadow-2xl" style={{
          left: SHELL_X,
          top: SHELL_Y,
          width: SHELL_W,
          height: SHELL_H,
          zIndex: 1,
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)'
        }} initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: impactMode ? 0.15 : 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-2374">
            {/* Top bar */}
            <div className="flex items-center justify-between bg-gray-50 border-b border-gray-200 px-4" style={{
            height: TOPBAR_H
          }} data-id="element-2375">
              <div className="flex items-center gap-3" data-id="element-2376">
                <ChevronLeft className="w-4 h-4 text-gray-500" data-id="element-2377" />
                <div className="text-sm font-semibold text-gray-900" data-id="element-2378">
                  Untitled Workflow
                </div>
                <div className="px-2 py-0.5 bg-gray-200 rounded-full text-[9px] font-bold tracking-wider uppercase text-gray-600" data-id="element-2379">
                  Draft
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 rounded-lg text-xs font-semibold text-white" data-id="element-2380">
                <Save className="w-3.5 h-3.5" data-id="element-2381" />
                Save
              </div>
            </div>

            <div className="flex" style={{
            height: SHELL_H - TOPBAR_H
          }} data-id="element-2382">
              {/* Sidebar */}
              <div className="bg-white border-r border-gray-200 overflow-hidden" style={{
              width: SIDEBAR_W
            }} data-id="element-2383">
                <div className="p-3 space-y-4" data-id="element-2384">
                  {SIDEBAR.map(section => <div key={section.category} data-id="element-2385">
                      <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-400 mb-1.5 px-2" data-id="element-2386">
                        {section.category}
                      </div>
                      <div className="space-y-0.5" data-id="element-2387">
                        {section.items.map(item => <SidebarPill key={item.id} item={item} hover={sidebarHover(item.id)} data-id="element-2388" />)}
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Canvas */}
              <div className="relative flex-1 overflow-hidden" style={{
              backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.08) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
              backgroundColor: '#fafafa'
            }} data-id="element-2389">
                {/* SVG layer for edges (z:2) */}
                <svg className="absolute inset-0 pointer-events-none" style={{
                zIndex: 2
              }} width={CANVAS_W} height={CANVAS_H} data-id="element-2390">
                  {showPhone && t >= T.C1_DROP && <PermanentEdge from={phoneR} to={agentL} drawTime={T.C1_DROP} glow={flowGlow} t={t} data-id="element-2391" />}
                  {showWidget && t >= T.C2_DROP && <PermanentEdge from={widgetR} to={agentL} drawTime={T.C2_DROP} glow={flowGlow} t={t} data-id="element-2392" />}
                  {showAgent && t >= T.C3_DROP && <PermanentEdge from={agentR} to={kbL} drawTime={T.C3_DROP} glow={flowGlow} t={t} data-id="element-2393" />}
                  {showAgent && t >= T.C4_DROP && <PermanentEdge from={agentR} to={emailL} drawTime={T.C4_DROP} glow={flowGlow} t={t} data-id="element-2394" />}

                  {/* Drawing edge */}
                  {drawing && <path d={bezierPath(drawing.from, drawing.to)} stroke="#10D96A" strokeWidth={2} strokeDasharray="6 4" fill="none" opacity={0.85} data-id="element-2395" />}

                  {/* Flow shimmer when complete */}
                  {flowGlow && <>
                      <FlowShimmer from={phoneR} to={agentL} delay={0} data-id="element-2396" />
                      <FlowShimmer from={widgetR} to={agentL} delay={0.5} data-id="element-2397" />
                      <FlowShimmer from={agentR} to={kbL} delay={1.0} data-id="element-2398" />
                      <FlowShimmer from={agentR} to={emailL} delay={1.5} data-id="element-2399" />
                    </>}
                </svg>

                {/* Nodes */}
                {showPhone && <FlowNode node={NODES.phone} glow={flowGlow} dropTime={T.PHONE_DROP} t={t} data-id="element-2400" />}
                {showWidget && <FlowNode node={NODES.widget} glow={flowGlow} dropTime={T.WIDGET_DROP} t={t} data-id="element-2401" />}
                {showAgent && <FlowNode node={NODES.agent} glow={flowGlow} dropTime={T.AGENT_DROP} t={t} data-id="element-2402" />}
                {showKb && <FlowNode node={NODES.kb} glow={flowGlow} dropTime={T.KB_DROP} t={t} data-id="element-2403" />}
                {showEmail && <FlowNode node={NODES.email} glow={flowGlow} dropTime={T.EMAIL_DROP} t={t} data-id="element-2404" />}
              </div>
            </div>
          </motion.div>

          {/* Drag ghost (stage-level positioning) */}
          {ghost && <motion.div className="absolute pointer-events-none" style={{
          left: ghost.x - 80,
          top: ghost.y - 16,
          zIndex: 40,
          width: 160,
          transform: 'rotate(-2deg)'
        }} initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 0.9,
          scale: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2405">
              <SidebarPill item={ghost.item} hover={false} ghost data-id="element-2406" />
            </motion.div>}

          {/* Floating tag */}
          <AnimatePresence mode="wait" data-id="element-2407">
            {tag && <motion.div key={tag.key} className="absolute pointer-events-none" style={{
            left: tag.x,
            top: tag.y,
            zIndex: 30
          }} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -4
          }} transition={{
            duration: 0.35
          }} data-id="element-2408">
                <div className="px-3 py-1.5 bg-emerald-500/90 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase text-white whitespace-nowrap shadow-lg" data-id="element-2409">
                  {tag.text}
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Cursor */}
          {cursor.visible && !impactMode && <motion.div className="absolute pointer-events-none" style={{
          left: cursor.x,
          top: cursor.y,
          zIndex: 50
        }} animate={{
          scale: cursor.clicking ? 0.85 : 1
        }} transition={{
          duration: 0.12
        }} data-id="element-2410">
              <MousePointer2 className="w-6 h-6 text-white" style={{
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
            transform: 'translate(-2px, -2px)'
          }} fill="white" data-id="element-2411" />
              {cursor.clicking && <motion.div className="absolute top-0 left-0 w-6 h-6 rounded-full border-2 border-emerald-400" initial={{
            scale: 0.5,
            opacity: 0.8
          }} animate={{
            scale: 2,
            opacity: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-2412" />}
            </motion.div>}

          {/* Top caption */}
          <AnimatePresence mode="wait" data-id="element-2413">
            {caption && !impactMode && <motion.div key={caption.id} className="absolute left-1/2 -translate-x-1/2 text-center" style={{
            top: 8,
            zIndex: 60
          }} initial={{
            opacity: 0,
            y: -8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-2414">
                <div className="text-sm font-light tracking-tight text-white/75" data-id="element-2415">
                  {caption.text}
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Impact takeover */}
          {impactMode && <>
              <div className="absolute inset-0" style={{
            zIndex: 70,
            background: 'radial-gradient(circle at center, rgba(6,8,15,0.85) 0%, rgba(6,8,15,0.98) 100%)'
          }} data-id="element-2416" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-5" style={{
            zIndex: 71
          }} data-id="element-2417">
                <ImpactWord text="No code." start={T.IMPACT_1} t={t} color="text-white/45" data-id="element-2418" />
                <ImpactWord text="No complexity." start={T.IMPACT_2} t={t} color="text-white/45" data-id="element-2419" />
                <ImpactWord text="Full control." start={T.IMPACT_3} t={t} size="large" data-id="element-2420" />
                {t >= T.IMPACT_FINAL && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-2xl font-light tracking-tight text-emerald-400 mt-6" data-id="element-2421">
                    Design your AI. Your way.
                  </motion.div>}
              </div>
            </>}
        </div>
      </div>

      {/* Chrome */}
      {!hideChrome && <>
          <div className="absolute top-6 right-6 z-[80] flex items-center gap-2" data-id="element-2422">
            <button onClick={() => setPlaying(p => !p)} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.06] hover:bg-white/[0.12] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/85 hover:text-white transition-all" data-id="element-2423">
              {playing ? <>
                  <Pause className="w-3.5 h-3.5" data-id="element-2424" /> Pause
                </> : <>
                  <Play className="w-3.5 h-3.5" data-id="element-2425" /> Play
                </>}
            </button>
            <button onClick={replay} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.06] hover:bg-white/[0.12] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/85 hover:text-white transition-all" data-id="element-2426">
              <RotateCcw className="w-3.5 h-3.5" data-id="element-2427" /> Replay
            </button>
          </div>

          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-[80] w-full max-w-2xl px-6" data-id="element-2428">
            <div className="relative h-1.5 rounded-full bg-white/[0.07] overflow-hidden" data-id="element-2429">
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-400 via-emerald-300 to-amber-300 rounded-full" style={{
            width: `${t / SLIDE_7_DURATION * 100}%`
          }} data-id="element-2430" />
            </div>
            <div className="flex items-center justify-between mt-2 text-[9px] font-bold tracking-[0.25em] text-white/45 uppercase" data-id="element-2431">
              <span className="font-mono" data-id="element-2432">
                {Math.floor(t / 60)}:
                {String(Math.floor(t % 60)).padStart(2, '0')}
              </span>
              <span data-id="element-2433">Flow Builder</span>
              <span className="font-mono" data-id="element-2434">
                {Math.floor(SLIDE_7_DURATION / 60)}:
                {String(Math.floor(SLIDE_7_DURATION % 60)).padStart(2, '0')}
              </span>
            </div>
          </div>
        </>}
    </div>;
}